import { z } from 'zod';
import { stateKeySchema } from '../catalogue/state-rules.js';
import { qty, qtyString, qtyToString } from '../quantity/index.js';
import { isIsoInstant, isPlainDate, toDate } from '../time/index.js';

/**
 * The state key for stock of a product without a state rule (flour, packaging): one
 * state, sellable and usable. Products with a rule use their rule's keys.
 */
export const DEFAULT_STOCK_STATE = 'default';

/**
 * The state of stock that arrived past its best-before when its product's rule has no
 * blocked state to put it in (D-068, CR6-104; see `expiredArrivalState`). No rule gives
 * it, so nothing treats it as usable or sellable.
 */
export const EXPIRED_STOCK_STATE = 'expired';

/**
 * Why stock moved. The set is closed: a new reason is a code change, never free text.
 * `replay_*` reasons are the **synthetic** movements the sales-replay harness invents
 * (opening stock and production derived from sales). Replayed sales themselves are real
 * facts and use `sale` / `sale_reversal` / `refund_return` with provenance
 * `external_observed`. `fulfilment` is a customer order handed over (D-076): the goods leave
 * with the customer (manual orders are not sales tickets; nothing else deducts them in V1).
 */
export const REASON_CODES = [
  'opening',
  'receipt',
  'production_output',
  'production_consumption',
  'sale',
  'sale_reversal',
  'refund_return',
  'count_adjustment',
  'write_off',
  'transfer_out',
  'transfer_in',
  'state_change',
  'correction',
  'fulfilment',
  'replay_opening',
  'replay_production_output',
  'replay_production_consumption',
] as const;
/** One of {@link REASON_CODES}. */
export type ReasonCode = (typeof REASON_CODES)[number];

/** The direction a reason normally moves stock: `in` (+), `out` (−) or `either`. */
export type ReasonDirection = 'in' | 'out' | 'either';

/**
 * Expected sign per reason. A movement against its reason's direction is applied (it is a
 * physical fact) but flagged `unexpected_sign`, because it usually means a bug upstream.
 */
export const REASON_DIRECTION: Readonly<Record<ReasonCode, ReasonDirection>> = {
  opening: 'in',
  receipt: 'in',
  production_output: 'in',
  production_consumption: 'out',
  sale: 'out',
  sale_reversal: 'in',
  refund_return: 'in',
  count_adjustment: 'either',
  write_off: 'out',
  transfer_out: 'out',
  transfer_in: 'in',
  state_change: 'either',
  correction: 'either',
  fulfilment: 'out',
  replay_opening: 'in',
  replay_production_output: 'in',
  replay_production_consumption: 'out',
};

/** True for the synthetic reasons only the replay harness writes. */
export function isReplayReason(reason: ReasonCode): boolean {
  return reason.startsWith('replay_');
}

/**
 * Where a figure came from (vault Core Model change 3): `counted` by a person, `system`
 * derived by ENP (FEFO consumption, state changes), `adjusted` by a person's correction,
 * `external_observed` read from another system (a replayed sale).
 */
export const PROVENANCES = ['counted', 'system', 'adjusted', 'external_observed'] as const;
/** One of {@link PROVENANCES}. */
export type Provenance = (typeof PROVENANCES)[number];

/** Who recorded a movement. `system` = a scheduled job or the replay harness. */
export const ACTOR_KINDS = ['staff', 'admin', 'integration', 'system'] as const;
/** One of {@link ACTOR_KINDS}. */
export type ActorKind = (typeof ACTOR_KINDS)[number];

const nonEmpty = z.string().min(1);

/** zod schema for a movement's actor. `deviceId` is set for staff acting on a station. */
export const movementActorSchema = z.object({
  kind: z.enum(ACTOR_KINDS),
  id: nonEmpty,
  deviceId: nonEmpty.optional(),
});
/** A movement's actor. */
export type MovementActor = z.infer<typeof movementActorSchema>;

/** zod schema for the document a movement came from (`goods_receipt`, `sale_ticket`, …). */
export const sourceDocSchema = z.object({
  type: z.string().regex(/^[a-z][a-z0-9_]*$/, 'snake_case document type'),
  id: nonEmpty,
});
/** The document a movement came from. */
export type SourceDoc = z.infer<typeof sourceDocSchema>;

/**
 * zod schema for one ledger movement (plan §5.3 `stock_movement`). Quantities are decimal
 * strings, instants ISO-8601 with an offset, the trading day a plain date.
 */
export const movementSchema = z.object({
  /** Per-account idempotency key: `cmd:<id>:<n>`, `sale:…`, `comp:<key>`. */
  idempotencyKey: nonEmpty,
  productId: nonEmpty,
  /** `null` only for stock that is not tracked by lot. */
  lotId: nonEmpty.nullable(),
  locationId: nonEmpty,
  state: stateKeySchema,
  /** Signed decimal string, in the product's base unit. */
  qtyDelta: qtyString,
  reason: z.enum(REASON_CODES),
  sourceDoc: sourceDocSchema,
  actor: movementActorSchema,
  occurredAt: z.string().refine(isIsoInstant, 'ISO-8601 instant with an offset'),
  recordedAt: z.string().refine(isIsoInstant, 'ISO-8601 instant with an offset'),
  tradingDay: z.string().refine(isPlainDate, 'YYYY-MM-DD'),
  provenance: z.enum(PROVENANCES),
  /** Idempotency key of the movement this one reverses (see `compensate`). */
  compensates: nonEmpty.nullable().optional(),
});
/** One ledger movement. */
export type Movement = z.infer<typeof movementSchema>;

/** Throws `RangeError` naming the first problems when `m` is not a well-formed movement. */
export function assertMovement(m: Movement): void {
  const parsed = movementSchema.safeParse(m);
  if (!parsed.success) {
    const where = typeof m?.idempotencyKey === 'string' ? m.idempotencyKey : '(no key)';
    const detail = parsed.error.issues
      .slice(0, 3)
      .map((i) => `${i.path.join('.')}: ${i.message}`)
      .join('; ');
    throw new RangeError(`Invalid movement ${where}: ${detail}`);
  }
}

/**
 * The identity of a movement's payload, used to tell a harmless replay (same key, same
 * fact) from a conflict (same key, different fact). Quantities compare numerically
 * (`"1.50"` = `"1.5"`), instants by the moment they denote. Two fields are **excluded**:
 * `recordedAt` (when a server wrote the fact down, so a re-send legitimately differs) and
 * `tradingDay` (derived from `occurredAt` by the account's zone and cut-off: a settings
 * change between a send and its re-send must not turn a harmless repeat into a conflict;
 * `occurredAt` itself is in — review CR2-7).
 */
export function movementFingerprint(m: Movement): string {
  return JSON.stringify([
    m.idempotencyKey,
    m.productId,
    m.lotId,
    m.locationId,
    m.state,
    qtyToString(qty(m.qtyDelta)),
    m.reason,
    m.sourceDoc.type,
    m.sourceDoc.id,
    m.actor.kind,
    m.actor.id,
    m.actor.deviceId ?? null,
    toDate(m.occurredAt).toISOString(),
    m.provenance,
    m.compensates ?? null,
  ]);
}

/** The balance a movement lands in: (product, lot, location, state). */
export interface BalanceKeyParts {
  productId: string;
  lotId: string | null;
  locationId: string;
  state: string;
}

/**
 * A collision-proof map key for a balance row (JSON of the four parts, so no id containing
 * a separator can forge another row's key).
 */
export function balanceKey(parts: BalanceKeyParts): string {
  return JSON.stringify([parts.productId, parts.lotId, parts.locationId, parts.state]);
}
